import React, { useMemo } from 'react';
import {
  Col, DatePicker, Divider, Form, FormItemProps, Input, Row, Select, Typography,
} from 'antd';
import { Link } from 'react-router-dom';
import { dateFormat } from '../../../../contstant';
import AlternativePhoneInput from '../../../Common/AlternativePhoneInput';
import { BusinessPartnersCreateParams, BusinessPartnerWithContracts } from '../../../../hooks/api/businessPartners';
import { OPTIONS_SALUTATION, OPTIONS_TITLE } from '../../../../types/businessPartnerRelated';
import DetailList from '../../../Common/DetailList';

// const validationRules = createRulesForAntd(validationUpdateBusinessPartner);

interface CreateHiddenFieldsProps {
  prefix: string[], // FieldProps['name']
  fieldNames: string[],
}
/** Function which creates list of Input fields, which not displayed, but should exist to store form-data */
const createHiddenFields = ({ prefix, fieldNames }: CreateHiddenFieldsProps): ModifiedFormItemProps[] => (
  fieldNames.map((fieldName) => (
    {
      name: [...prefix, fieldName],
      element: <Input disabled />,
    }
  ))
);

interface ModifiedFormItemProps extends FormItemProps {
  element: React.ReactNode;
  colSpan?: number;
  isHidden?: boolean;
}

function ColItem({
  element, colSpan, name, rules, isHidden, ...formItemProps
}: ModifiedFormItemProps) {
  return (
    <Col key={name} span={colSpan || 12} style={isHidden ? { display: 'none' } : undefined}>
      <Form.Item name={name} {...formItemProps} rules={[...(rules || [])]}>
        {/* TODO add? rules={[...(rules || []), validationRules]} */}
        {element}
      </Form.Item>
    </Col>
  );
}

interface BusinessFormProps {
  data?: Partial<BusinessPartnersCreateParams>;
  businessPartnerData: BusinessPartnerWithContracts | undefined;
}

function BusinessFormItems({ data, businessPartnerData }: BusinessFormProps) {
  const hiddenFormItems: ModifiedFormItemProps[] = useMemo(() => ([
    ...createHiddenFields({
      prefix: ['personalData', 'businessPartner'],
      fieldNames: ['businessPartnerNumber', 'newLegalForm', 'newSalutation', 'newTitle'],
    }),
    ...createHiddenFields({
      prefix: ['personalData', 'emailAddress'],
      fieldNames: ['businessPartnerNumber'],
    }),
    ...createHiddenFields({
      prefix: ['personalData', 'phoneNumber'],
      fieldNames: ['businessPartner', 'creationDate', 'advertisementConsentROList'],
    }),
    ...createHiddenFields({
      prefix: ['billingAddress'],
      fieldNames: ['businessPartnerNumber'],
    }),
    ...createHiddenFields({
      prefix: ['billingAddress', 'adressPortal'],
      fieldNames: ['changedCreateE03', 'id'],
    }),
  ]
  ), [data]);

  const formItems: ModifiedFormItemProps[] = useMemo(() => [
    {
      name: ['personalData', 'businessPartner', 'newFirstname'],
      label: 'First Name',
      rules: [{ required: true, message: 'Required' }],
      element: <Input placeholder="Enter" />,
    },
    {
      name: ['personalData', 'businessPartner', 'newLastname'],
      label: 'Last Name',
      rules: [{ required: true, message: 'Required' }],
      element: <Input placeholder="Enter" />,
    },
    {
      name: ['personalData', 'businessPartner', 'newDateOfBirth'],
      label: 'Date of birth',
      rules: [{ required: true, message: 'Required' }],
      element: <DatePicker format={dateFormat} style={{ width: '100%' }} allowClear={false} />,
    },
    {
      name: ['personalData', 'emailAddress', 'newEmailAddress'],
      label: 'Email',
      rules: [{ required: true }, { type: 'email', message: 'Invalid email format' }],
      element: <Input placeholder="Enter" />,
    },
    {
      name: ['personalData', 'phoneNumber', 'number'],
      label: 'Phone',
      element: <AlternativePhoneInput />,
    },
    {
      name: ['personalData', 'businessPartner', 'newSalutation'],
      label: 'Salutation',
      rules: [{ required: true }],
      element: <Select placeholder="Select" options={OPTIONS_SALUTATION} />,
    },
    {
      name: ['personalData', 'businessPartner', 'newTitle'],
      label: 'Title',
      rules: [{ required: true }],
      element: <Select placeholder="Select" options={OPTIONS_TITLE} />,
    },
  ], [data]);

  const addressFormItems: ModifiedFormItemProps[] = useMemo(() => [
    {
      name: ['billingAddress', 'adressPortal', 'city'],
      label: 'City',
      element: <Input placeholder="Enter" />,
    },
    {
      name: ['billingAddress', 'adressPortal', 'street'],
      label: 'Street',
      element: <Input placeholder="Enter" />,
    },
    {
      name: ['billingAddress', 'adressPortal', 'housenumber'],
      label: 'House number',
      element: <Input placeholder="Enter" />,
    },
    {
      name: ['billingAddress', 'adressPortal', 'housenumberAddition'],
      label: 'House number addition',
      element: <Input placeholder="Enter (optional)" />,
    },
    {
      name: ['billingAddress', 'adressPortal', 'postalCode'],
      label: 'Postal code',
      element: <Input placeholder="Enter" />,
    },
  ], [data]);

  return (
    <div>
      <Row gutter={12}>
        <Col span={24}>
          <Divider>Personal data</Divider>
        </Col>
        <DetailList items={[
          // data?.personalData?.businessPartner?.businessPartnerNumber
          { label: 'Business partner #', value: businessPartnerData?.businessPartnerNumber, copyable: true },
          { label: 'Business partner ID', value: businessPartnerData?.businessPartnerId },
          { label: 'Metering point', value: businessPartnerData?.meteringPoint },
          { label: 'Device number', value: businessPartnerData?.deviceNumber },
        ]}
        />
        {formItems.map((props) => <ColItem key={props.name} {...props} />)}
        <Col span={12}>
          <Typography.Text strong>User</Typography.Text>
          <p>
            <Link to={`/users/${businessPartnerData?.user?.id}/edit`}>Go to user page</Link>
          </p>
        </Col>

        <Col span={24}>
          <Divider>Address</Divider>
        </Col>
        {addressFormItems.map((props) => <ColItem key={props.name} {...props} />)}

        {hiddenFormItems.map((props) => <ColItem isHidden key={props.name} {...props} />)}
      </Row>
    </div>
  );
}

export default BusinessFormItems;
